import React, { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const TEMPLATE = "nome,email,telefone,cpf,cep,rua,numero,complemento,bairro,cidade,uf\nJoão Silva,joao@email.com,21999990000,,22011-002,Av. Atlântica,100,Apto 10,Copacabana,Rio de Janeiro,RJ\n";

export default function ImportCustomersDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "modelo_clientes.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.summary.created} cliente(s) importado(s).`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="import-customers-button">
          <Upload className="h-4 w-4" /> Importar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="import-customers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#1e7be0]" /> Importar clientes (CSV)</DialogTitle>
          <DialogDescription>Cada cliente recebe uma senha provisória. Colunas: nome, email, telefone, cep, rua, cidade, uf (obrigatórias).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button variant="outline" className="w-full gap-1.5" onClick={downloadTemplate} data-testid="import-template-button">
            <Download className="h-4 w-4" /> Baixar modelo (CSV)
          </Button>
          <label className="block">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="import-file-input" />
            <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy}>
              <span onClick={() => fileRef.current?.click()} data-testid="import-select-file-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Importando..." : "Selecionar arquivo CSV"}
              </span>
            </Button>
          </label>

          {result && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2 text-sm" data-testid="import-result">
              <p className="flex items-center gap-1.5 text-green-700"><CheckCircle2 className="h-4 w-4" /> {result.summary.created} criado(s)</p>
              {result.summary.skipped > 0 && <p className="text-amber-700">{result.summary.skipped} ignorado(s) (já cadastrados)</p>}
              {result.summary.errors > 0 && (
                <div className="text-red-600">
                  <p className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {result.summary.errors} com erro:</p>
                  <ul className="list-disc pl-6 text-xs">
                    {result.errors.slice(0, 8).map((er, i) => <li key={i}>Linha {er.line}: {er.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
